import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { deliveriesAPI, customersAPI, excavatorsAPI, driversAPI, exportAPI } from "../lib/api";
import { formatDate } from "../lib/dateUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { AutocompleteInput } from "../components/AutocompleteInput";
import { DatePicker } from "../components/DatePicker";
import { SearchableSelect } from "../components/SearchableSelect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Truck,
  Plus,
  MapPin,
  Calendar,
  User,
  Eye,
  Trash2,
  ExternalLink,
  Phone,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Filter,
  Download,
  X,
  Edit,
  Car,
  Navigation,
  Package,
  Ban,
  ArrowUp,
  ArrowDown,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const Deliveries = () => {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [excavators, setExcavators] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [suggestions, setSuggestions] = useState({
    pickup_locations: [],
    delivery_locations: [],
    pickup_contacts: [],
    delivery_contacts: [],
    pickup_phones: [],
    delivery_phones: []
  });
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  
  // Filter state - initialize date to today for drivers
  const getInitialDate = () => {
    // This will be called during initial render, user might not be loaded yet
    return "";
  };
  
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(getInitialDate);
  const [dateTo, setDateTo] = useState(getInitialDate);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showExpired, setShowExpired] = useState("all");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [driverDateInitialized, setDriverDateInitialized] = useState(false);
  const searchTimeoutRef = useRef(null);
  
  // Sorting
  const [sortBy, setSortBy] = useState("scheduled_date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Set default date to today for drivers
  useEffect(() => {
    if (user?.role === "driver" && !driverDateInitialized) {
      const today = new Date().toISOString().split("T")[0];
      setDateFrom(today);
      setDateTo(today);
      setDriverDateInitialized(true);
    }
  }, [user?.role, driverDateInitialized]);

  const [formData, setFormData] = useState({
    customer_id: "",
    excavator_id: "",
    pickup_location: "",
    pickup_contact_person: "",
    pickup_contact_number: "",
    delivery_location: "",
    delivery_contact_person: "",
    delivery_contact_number: "",
    billing_type: "chargeable",
    delivery_type: "Transportation",
    assigned_driver_id: "",
    scheduled_date: "",
    scheduled_time: "",
    notes: "",
  });

  const DELIVERY_TYPES = ["Sales", "Rental", "Transportation", "Exchange"];

  const TIME_SLOTS = [
    { value: "0830", label: "08:30" },
    { value: "1030", label: "10:30" },
    { value: "1230", label: "12:30" },
    { value: "1430", label: "14:30" },
    { value: "1630", label: "16:30" },
    { value: "1830", label: "18:30" },
  ];

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isDriver = user?.role === "driver";
  const canCreate = isAdmin || isManager;
  const canEdit = isAdmin || isManager;
  const canExport = isAdmin || isManager;

  // Sort deliveries client-side
  const sortedDeliveries = useMemo(() => {
    if (!deliveries.length) return deliveries;
    
    return [...deliveries].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case "scheduled_date":
          aVal = a.scheduled_date || "";
          bVal = b.scheduled_date || "";
          break;
        case "customer_name":
          aVal = (a.customer_name || "").toLowerCase();
          bVal = (b.customer_name || "").toLowerCase();
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "pickup_location":
          aVal = (a.pickup_location || "").toLowerCase();
          bVal = (b.pickup_location || "").toLowerCase();
          break;
        case "distance_km":
          aVal = a.distance_km || 0;
          bVal = b.distance_km || 0;
          break;
        default:
          aVal = a.scheduled_date || "";
          bVal = b.scheduled_date || "";
      }
      
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  }, [deliveries, sortBy, sortOrder]);

  // Toggle sort
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Fetch deliveries with filters
  const fetchDeliveries = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
      };
      
      if (statusFilter !== "all") params.status = statusFilter;
      if (customerFilter !== "all") params.customer_id = customerFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (debouncedSearch) params.search = debouncedSearch;
      if (showExpired !== "all") params.show_expired = showExpired === "expired";

      const res = await deliveriesAPI.getAll(params);
      setDeliveries(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      toast.error("Failed to load deliveries");
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, customerFilter, dateFrom, dateTo, debouncedSearch, showExpired]);

  // Fetch reference data (customers, excavators, drivers)
  const fetchReferenceData = useCallback(async () => {
    try {
      const [customersRes, excavatorsRes, suggestionsRes] = await Promise.all([
        customersAPI.getAll(),
        excavatorsAPI.getAll(),
        deliveriesAPI.getSuggestions(),
      ]);
      setCustomers(customersRes.data);
      setExcavators(excavatorsRes.data);
      setSuggestions(suggestionsRes.data);

      if (isAdmin || isManager) {
        try {
          const driversRes = await driversAPI.getAll();
          setDrivers(driversRes.data);
        } catch (e) {
          console.log("No drivers endpoint or no drivers");
        }
      }
    } catch (error) {
      toast.error("Failed to load reference data");
    }
  }, [isAdmin, isManager]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    // For drivers, wait until date filter is initialized before fetching
    if (user?.role === "driver" && !driverDateInitialized) {
      return;
    }
    fetchDeliveries();
  }, [fetchDeliveries, user?.role, driverDateInitialized]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, customerFilter, dateFrom, dateTo, searchTerm, showExpired]);

  const resetForm = () => {
    setFormData({
      customer_id: "",
      excavator_id: "",
      pickup_location: "",
      pickup_contact_person: "",
      pickup_contact_number: "",
      delivery_location: "",
      delivery_contact_person: "",
      delivery_contact_number: "",
      billing_type: "chargeable",
      delivery_type: "Transportation",
      assigned_driver_id: "",
      scheduled_date: "",
      scheduled_time: "",
      notes: "",
    });
    setEditingDelivery(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.excavator_id || !formData.pickup_location || !formData.delivery_location) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Require driver assignment for new deliveries
    if (!editingDelivery && !formData.assigned_driver_id) {
      toast.error("Please assign a driver");
      return;
    }

    try {
      if (editingDelivery) {
        await deliveriesAPI.update(editingDelivery.id, formData);
        toast.success("Delivery updated successfully");
      } else {
        await deliveriesAPI.create(formData);
        toast.success("Delivery created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingDelivery ? "Failed to update delivery" : "Failed to create delivery"));
    }
  };

  const handleEdit = (delivery) => {
    setEditingDelivery(delivery);
    setFormData({
      customer_id: delivery.customer_id || "",
      excavator_id: delivery.excavator_id || "",
      pickup_location: delivery.pickup_location || "",
      pickup_contact_person: delivery.pickup_contact_person || "",
      pickup_contact_number: delivery.pickup_contact_number || "",
      delivery_location: delivery.delivery_location || "",
      delivery_contact_person: delivery.delivery_contact_person || "",
      delivery_contact_number: delivery.delivery_contact_number || "",
      billing_type: delivery.billing_type || "chargeable",
      delivery_type: delivery.delivery_type || "Transportation",
      assigned_driver_id: delivery.assigned_driver_id || "",
      scheduled_date: delivery.scheduled_date || "",
      scheduled_time: delivery.scheduled_time || "",
      notes: delivery.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deliveriesAPI.delete(id);
      toast.success("Delivery deleted");
      fetchDeliveries();
    } catch (error) {
      toast.error("Failed to delete delivery");
    }
  };

  const handleExport = async () => {
    try {
      toast.info("Preparing export...");
      await exportAPI.download("deliveries");
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleRecalculateDistances = async () => {
    try {
      setRecalculating(true);
      toast.info("Recalculating distances using Google Maps...");
      const res = await deliveriesAPI.recalculateDistances();
      const { updated, failed, skipped } = res.data;
      toast.success(`Distances updated: ${updated} updated, ${failed} failed, ${skipped} skipped`);
      fetchDeliveries(); // Refresh the list
    } catch (error) {
      toast.error("Failed to recalculate distances");
    } finally {
      setRecalculating(false);
    }
  };

  const handleCancelDelivery = async (deliveryId) => {
    try {
      await deliveriesAPI.update(deliveryId, { status: "canceled" });
      toast.success("Delivery canceled");
      fetchDeliveries();
    } catch (error) {
      toast.error("Failed to cancel delivery");
    }
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setCustomerFilter("all");
    setDateFrom("");
    setDateTo("");
    setSearchTerm("");
    setShowExpired("all");
  };

  const hasActiveFilters = statusFilter !== "all" || customerFilter !== "all" || dateFrom || dateTo || searchTerm || showExpired !== "all";

  const getStatusBadge = (status, isExpired) => {
    if (isExpired && status !== "completed" && status !== "canceled") {
      return "bg-red-100 text-red-800 border-red-300";
    }
    const styles = {
      pending: "bg-amber-100 text-amber-800 border-amber-300",
      in_progress: "bg-blue-100 text-blue-800 border-blue-300",
      completed: "bg-green-100 text-green-800 border-green-300",
      canceled: "bg-slate-100 text-slate-600 border-slate-400",
    };
    return styles[status] || styles.pending;
  };

  const getBillingBadge = (type) => {
    const styles = {
      chargeable: "bg-green-100 text-green-800 border-green-300",
      "non-chargeable": "bg-slate-100 text-slate-800 border-slate-300",
      partial: "bg-amber-100 text-amber-800 border-amber-300",
    };
    return styles[type] || styles.chargeable;
  };

  const getDeliveryTypeBadge = (type) => {
    const styles = {
      Sales: "bg-purple-100 text-purple-800 border-purple-300",
      Rental: "bg-cyan-100 text-cyan-800 border-cyan-300",
      Transportation: "bg-indigo-100 text-indigo-800 border-indigo-300",
      Exchange: "bg-pink-100 text-pink-800 border-pink-300",
    };
    return styles[type] || styles.Transportation;
  };

  if (loading && deliveries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deliveries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold uppercase tracking-tight">
            Deliveries
          </h1>
          <p className="text-slate-500 text-sm">
            {isDriver ? "Your assigned deliveries" : `${total} deliveries found`}
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {/* Recalculate Distances Button - Admin Only */}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={handleRecalculateDistances}
              disabled={recalculating}
              className="rounded-sm"
              data-testid="recalculate-distances-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
              {recalculating ? "Calculating..." : "Recalculate Distances"}
            </Button>
          )}
          {/* Export Button */}
          {canExport && (
            <Button
              variant="outline"
              onClick={handleExport}
              className="rounded-sm"
              data-testid="export-deliveries-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          {/* Create Button */}
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
                  data-testid="create-delivery-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Delivery
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase">
                    {editingDelivery ? "Edit Delivery" : "Create New Delivery"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Customer */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Customer *
                    </Label>
                    <SearchableSelect
                      value={formData.customer_id}
                      onChange={(v) => setFormData({ ...formData, customer_id: v })}
                      options={customers}
                      placeholder="Select customer"
                      searchPlaceholder="Search customers..."
                      displayKey="company"
                      valueKey="id"
                      data-testid="customer-select"
                    />
                  </div>

                  {/* Excavator */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Excavator *
                    </Label>
                    <SearchableSelect
                      value={formData.excavator_id}
                      onChange={(v) => setFormData({ ...formData, excavator_id: v })}
                      options={excavators.map(e => ({ id: e.id, name: `${e.model} - ${e.serial_number}` }))}
                      placeholder="Select excavator"
                      searchPlaceholder="Search excavators..."
                      displayKey="name"
                      valueKey="id"
                      data-testid="excavator-select"
                    />
                  </div>

                  {/* Pickup Location */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Pickup Location *
                    </Label>
                    <AutocompleteInput
                      value={formData.pickup_location}
                      onChange={(e) =>
                        setFormData({ ...formData, pickup_location: e.target.value })
                      }
                      suggestions={suggestions.pickup_locations}
                      placeholder="Enter pickup address"
                      className="rounded-sm"
                      data-testid="pickup-location"
                    />
                  </div>

                  {/* Pickup Contact Person */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Pickup Contact Person
                    </Label>
                    <AutocompleteInput
                      value={formData.pickup_contact_person}
                      onChange={(e) =>
                        setFormData({ ...formData, pickup_contact_person: e.target.value })
                      }
                      suggestions={suggestions.pickup_contacts}
                      placeholder="Contact name"
                      className="rounded-sm"
                      data-testid="pickup-contact-person"
                    />
                  </div>

                  {/* Pickup Contact Number */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Pickup Contact Number
                    </Label>
                    <AutocompleteInput
                      value={formData.pickup_contact_number}
                      onChange={(e) =>
                        setFormData({ ...formData, pickup_contact_number: e.target.value })
                      }
                      suggestions={suggestions.pickup_phones}
                      placeholder="Phone number"
                      className="rounded-sm"
                      data-testid="pickup-contact-number"
                    />
                  </div>

                  {/* Delivery Location */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Delivery Location *
                    </Label>
                    <AutocompleteInput
                      value={formData.delivery_location}
                      onChange={(e) =>
                        setFormData({ ...formData, delivery_location: e.target.value })
                      }
                      suggestions={suggestions.delivery_locations}
                      placeholder="Enter delivery address"
                      className="rounded-sm"
                      data-testid="delivery-location"
                    />
                  </div>

                  {/* Delivery Contact Person */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Delivery Contact Person
                    </Label>
                    <AutocompleteInput
                      value={formData.delivery_contact_person}
                      onChange={(e) =>
                        setFormData({ ...formData, delivery_contact_person: e.target.value })
                      }
                      suggestions={suggestions.delivery_contacts}
                      placeholder="Contact name"
                      className="rounded-sm"
                      data-testid="delivery-contact-person"
                    />
                  </div>

                  {/* Delivery Contact Number */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Delivery Contact Number
                    </Label>
                    <AutocompleteInput
                      value={formData.delivery_contact_number}
                      onChange={(e) =>
                        setFormData({ ...formData, delivery_contact_number: e.target.value })
                      }
                      suggestions={suggestions.delivery_phones}
                      placeholder="Phone number"
                      className="rounded-sm"
                      data-testid="delivery-contact-number"
                    />
                  </div>

                  {/* Billing Type */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Billing Type *
                    </Label>
                    <Select
                      value={formData.billing_type}
                      onValueChange={(v) => setFormData({ ...formData, billing_type: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chargeable">Chargeable</SelectItem>
                        <SelectItem value="non-chargeable">Non-Chargeable</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Delivery Type */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Delivery Type *
                    </Label>
                    <Select
                      value={formData.delivery_type}
                      onValueChange={(v) => setFormData({ ...formData, delivery_type: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Assigned Driver */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Assign Driver {!editingDelivery && "*"}
                    </Label>
                    <Select
                      value={formData.assigned_driver_id}
                      onValueChange={(v) =>
                        setFormData({ ...formData, assigned_driver_id: v === "none" ? "" : v })
                      }
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Select driver" />
                      </SelectTrigger>
                      <SelectContent>
                        {editingDelivery && <SelectItem value="none">-- No Driver --</SelectItem>}
                        {drivers.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name} {d.vehicle_number && `(${d.vehicle_number})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Scheduled Date */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Scheduled Date
                    </Label>
                    <DatePicker
                      value={formData.scheduled_date}
                      onChange={(date) =>
                        setFormData({ ...formData, scheduled_date: date })
                      }
                      placeholder="Select date (dd/mm/yyyy)"
                    />
                  </div>

                  {/* Time Slot */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Time Slot
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {TIME_SLOTS.map((slot) => (
                        <Button
                          key={slot.value}
                          type="button"
                          variant={formData.scheduled_time === slot.value ? "default" : "outline"}
                          className={`rounded-sm text-sm ${
                            formData.scheduled_time === slot.value
                              ? "bg-amber-500 hover:bg-amber-600 text-white"
                              : "border-slate-300"
                          }`}
                          onClick={() => setFormData({ ...formData, scheduled_time: slot.value })}
                        >
                          {slot.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">
                      Notes
                    </Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      className="rounded-sm"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
                  >
                    {editingDelivery ? "Update Delivery" : "Create Delivery"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by customer, location, excavator..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm"
              data-testid="deliveries-search"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`rounded-sm ${filtersExpanded ? 'bg-slate-100' : ''}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-2 bg-amber-500 text-white text-xs">Active</Badge>
            )}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="rounded-sm text-slate-500"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Expanded Filters */}
        {filtersExpanded && (
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Status Filter */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="rounded-sm" data-testid="status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Customer Filter */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Customer</Label>
                  <Select value={customerFilter} onValueChange={setCustomerFilter}>
                    <SelectTrigger className="rounded-sm" data-testid="customer-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date From */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Date From</Label>
                  <DatePicker
                    value={dateFrom}
                    onChange={(date) => setDateFrom(date)}
                    placeholder="From (dd/mm/yyyy)"
                  />
                </div>

                {/* Date To */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Date To</Label>
                  <DatePicker
                    value={dateTo}
                    onChange={(date) => setDateTo(date)}
                    placeholder="To (dd/mm/yyyy)"
                  />
                </div>

                {/* Expired Filter */}
                <div className="space-y-1">
                  <Label className="text-xs uppercase font-semibold text-slate-500">Expiry</Label>
                  <Select value={showExpired} onValueChange={setShowExpired}>
                    <SelectTrigger className="rounded-sm" data-testid="expired-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="expired">Expired Only</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sort Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500 font-medium">Sort by:</span>
          <div className="flex gap-1">
            <Button
              variant={sortBy === "scheduled_date" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("scheduled_date")}
              className={`rounded-sm text-xs ${sortBy === "scheduled_date" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              Date
              {sortBy === "scheduled_date" && (
                sortOrder === "desc" ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
              )}
            </Button>
            <Button
              variant={sortBy === "customer_name" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("customer_name")}
              className={`rounded-sm text-xs ${sortBy === "customer_name" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              Customer
              {sortBy === "customer_name" && (
                sortOrder === "desc" ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
              )}
            </Button>
            <Button
              variant={sortBy === "status" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("status")}
              className={`rounded-sm text-xs ${sortBy === "status" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              Status
              {sortBy === "status" && (
                sortOrder === "desc" ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
              )}
            </Button>
            <Button
              variant={sortBy === "distance_km" ? "default" : "outline"}
              size="sm"
              onClick={() => toggleSort("distance_km")}
              className={`rounded-sm text-xs ${sortBy === "distance_km" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              Distance
              {sortBy === "distance_km" && (
                sortOrder === "desc" ? <ArrowDown className="w-3 h-3 ml-1" /> : <ArrowUp className="w-3 h-3 ml-1" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Deliveries List */}
      {sortedDeliveries.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Truck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No deliveries found</p>
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="link"
                className="mt-2 text-amber-600"
              >
                Clear filters to see all deliveries
              </Button>
            )}
            {canCreate && !hasActiveFilters && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Delivery
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4">
            {sortedDeliveries.map((delivery) => (
              <Card
                key={delivery.id}
                className={`border-slate-200 hover:border-amber-300 transition-colors ${
                  delivery.is_expired ? 'border-l-4 border-l-red-500' : ''
                }`}
                data-testid={`delivery-card-${delivery.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      {/* Header Row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={`${getStatusBadge(delivery.status, delivery.is_expired)} rounded-none text-xs`}>
                          {delivery.is_expired && delivery.status !== "completed" && delivery.status !== "canceled" 
                            ? "EXPIRED" 
                            : delivery.status?.replace("_", " ").toUpperCase()}
                        </Badge>
                        {delivery.is_expired && delivery.status !== "completed" && delivery.status !== "canceled" && (
                          <span className="flex items-center text-xs text-red-600 font-medium">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Past scheduled date
                          </span>
                        )}
                        {/* Delivery Type Badge */}
                        {delivery.delivery_type && (
                          <Badge className={`${getDeliveryTypeBadge(delivery.delivery_type)} rounded-none text-xs`}>
                            <Package className="w-3 h-3 mr-1" />
                            {delivery.delivery_type.toUpperCase()}
                          </Badge>
                        )}
                        {/* Hide billing type for drivers */}
                        {!isDriver && (
                          <Badge className={`${getBillingBadge(delivery.billing_type)} rounded-none text-xs`}>
                            {delivery.billing_type?.replace("-", " ").toUpperCase()}
                          </Badge>
                        )}
                        {/* Distance Badge */}
                        {delivery.distance_km !== null && delivery.distance_km !== undefined && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300 rounded-none text-xs" title="Driving distance (Google Maps)">
                            <Navigation className="w-3 h-3 mr-1" />
                            {delivery.distance_km} KM
                          </Badge>
                        )}
                        {delivery.scheduled_date && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(delivery.scheduled_date)}
                            {delivery.scheduled_time && (
                              <span className="font-semibold text-amber-600">
                                @ {delivery.scheduled_time.slice(0, 2)}:{delivery.scheduled_time.slice(2)}
                              </span>
                            )}
                          </span>
                        )}
                      </div>

                      {/* Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{delivery.customer_name || "No customer"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4" />
                          <span>
                            {delivery.excavator_model} - {delivery.excavator_serial}
                          </span>
                        </div>
                        {delivery.assigned_driver_name && (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-500" />
                            <span className="text-blue-600 font-medium">
                              Driver: {delivery.assigned_driver_name}
                            </span>
                            {delivery.driver_vehicle_number && (
                              <span className="flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">
                                <Car className="w-3 h-3" />
                                {delivery.driver_vehicle_number}
                              </span>
                            )}
                          </div>
                        )}
                        {delivery.pickup_location && (
                          <div className="flex items-center gap-2 col-span-full">
                            <MapPin className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-green-700">From:</span>
                            <span className="truncate max-w-xs">{delivery.pickup_location}</span>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(delivery.pickup_location)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-500 text-white rounded-sm hover:bg-green-600"
                              title="Open in Google Maps"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                        {(delivery.pickup_contact_person || delivery.pickup_contact_number) && (
                          <div className="flex items-center gap-2 col-span-full ml-6">
                            <Phone className="w-3 h-3 text-green-600" />
                            <span className="text-sm text-green-700">
                              {delivery.pickup_contact_person}
                              {delivery.pickup_contact_person && delivery.pickup_contact_number && " - "}
                              {delivery.pickup_contact_number}
                            </span>
                          </div>
                        )}
                        {delivery.delivery_location && (
                          <div className="flex items-center gap-2 col-span-full">
                            <MapPin className="w-4 h-4 text-red-600" />
                            <span className="font-medium text-red-700">To:</span>
                            <span className="truncate max-w-xs">{delivery.delivery_location}</span>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(delivery.delivery_location)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-500 text-white rounded-sm hover:bg-red-600"
                              title="Open in Google Maps"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                        {(delivery.delivery_contact_person || delivery.delivery_contact_number) && (
                          <div className="flex items-center gap-2 col-span-full ml-6">
                            <Phone className="w-3 h-3 text-red-600" />
                            <span className="text-sm text-red-700">
                              {delivery.delivery_contact_person}
                              {delivery.delivery_contact_person && delivery.delivery_contact_number && " - "}
                              {delivery.delivery_contact_number}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {/* View/Start/Continue Button */}
                      {isDriver && delivery.is_expired && delivery.status !== "completed" && delivery.status !== "canceled" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm border-slate-300 opacity-50 cursor-not-allowed"
                          disabled
                          data-testid={`view-delivery-${delivery.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Expired
                        </Button>
                      ) : (
                        <Link to={`/deliveries/${delivery.id}`}>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-sm border-slate-300"
                            data-testid={`view-delivery-${delivery.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {isDriver && delivery.status === "pending"
                              ? "Start"
                              : isDriver && delivery.status === "in_progress"
                              ? "Continue"
                              : "View"}
                          </Button>
                        </Link>
                      )}
                      
                      {/* Edit Button */}
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(delivery)}
                          className="rounded-sm border-slate-300"
                          data-testid={`edit-delivery-${delivery.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}

                      {/* Cancel Button - Only for pending/in_progress deliveries */}
                      {canEdit && (delivery.status === "pending" || delivery.status === "in_progress") && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-sm border-orange-300 text-orange-600 hover:bg-orange-50"
                              data-testid={`cancel-delivery-${delivery.id}`}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel Delivery?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will mark the delivery as canceled. Are you sure?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm">No, keep it</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleCancelDelivery(delivery.id)}
                                className="bg-orange-500 hover:bg-orange-600 rounded-sm"
                              >
                                Yes, Cancel
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      
                      {/* Delete Button */}
                      {isAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-sm border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`delete-delivery-${delivery.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Delivery?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(delivery.id)}
                                className="bg-red-500 hover:bg-red-600 rounded-sm"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-500">
                Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-sm"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className="rounded-sm"
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Deliveries;
